import React, { useCallback, useEffect, useRef, useState } from "react";
import { Sprite, SpeakerIcon, StarPips, CaptionChip } from "./fx";
import ChooseKind from "./ChooseKind";

// BreatheTrace — balloon breathing on the orb. Press & hold; a ring traces
// inhale(4s) -> hold(1.2s) -> exhale(4s); the orb scales 0.64 -> 1.0 -> 0.64;
// motes drift up. Progress only advances while holding (lifting pauses, never
// punishes). Shows one pip per breath. Can chain into a ChooseKind follow-up.

const DURATIONS = { inhale: 4, hold: 1.2, exhale: 4 };
const LABELS = { inhale: "Breathe in…", hold: "Hold it…", exhale: "Breathe out…" };
const R = 130;
const C = 2 * Math.PI * R;
const MOTES = [0, 1, 2, 3, 4, 5];

const BreatheTrace = ({ payload, sounds, onDone, prompt, speakPrompt }) => {
  const [phase, setPhase] = useState("inhale");
  const [progress, setProgress] = useState(0);
  const [breathsDone, setBreathsDone] = useState(0);
  const [holding, setHolding] = useState(false);
  const [showFollowUp, setShowFollowUp] = useState(false);

  const phaseRef = useRef("inhale");
  const progressRef = useRef(0);
  const breathsRef = useRef(0);
  const holdingRef = useRef(false);
  const finishedRef = useRef(false);
  const rafRef = useRef(0);
  const lastTsRef = useRef(0);

  const finish = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    sounds.playPop(0.55);
    sounds.speakSoothing("Ahh, nice and calm.");
    window.setTimeout(() => {
      if (payload.followUp) setShowFollowUp(true);
      else onDone();
    }, 1200);
  }, [onDone, payload.followUp, sounds]);

  const advancePhase = useCallback(() => {
    progressRef.current = 0;
    setProgress(0);
    const current = phaseRef.current;
    if (current === "inhale") {
      phaseRef.current = "hold";
      setPhase("hold");
    } else if (current === "hold") {
      phaseRef.current = "exhale";
      setPhase("exhale");
      sounds.speakSoothing("Breathe out");
    } else {
      breathsRef.current += 1;
      setBreathsDone(breathsRef.current);
      if (breathsRef.current >= payload.breaths) {
        finish();
      } else {
        phaseRef.current = "inhale";
        setPhase("inhale");
        sounds.speakSoothing("Breathe in");
      }
    }
  }, [finish, payload.breaths, sounds]);

  useEffect(() => {
    const tick = (ts) => {
      const last = lastTsRef.current || ts;
      const dt = (ts - last) / 1000;
      lastTsRef.current = ts;
      if (holdingRef.current && !finishedRef.current) {
        const dur = DURATIONS[phaseRef.current];
        const nextProgress = Math.min(1, progressRef.current + dt / dur);
        progressRef.current = nextProgress;
        setProgress(nextProgress);
        if (nextProgress >= 1) advancePhase();
      }
      rafRef.current = window.requestAnimationFrame(tick);
    };
    rafRef.current = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(rafRef.current);
  }, [advancePhase]);

  const setHold = useCallback((value, event) => {
    if (event && value) event.currentTarget.setPointerCapture?.(event.pointerId);
    holdingRef.current = value;
    lastTsRef.current = 0;
    setHolding(value);
  }, []);

  if (showFollowUp && payload.followUp) {
    return (
      <ChooseKind
        payload={payload.followUp}
        sounds={sounds}
        onDone={onDone}
        prompt={payload.followUp.prompt}
        speakPrompt={() => sounds.speak(payload.followUp.prompt)}
      />
    );
  }

  const eased = 0.5 - 0.5 * Math.cos(progress * Math.PI);
  let orbScale = 1;
  if (phase === "inhale") orbScale = 0.64 + 0.36 * eased;
  else if (phase === "exhale") orbScale = 1 - 0.36 * eased;

  return (
    <div className="ch-activity-body ch-breathe">
      {prompt && (
        <div className="ch-prompt-capsule">
          <span>{prompt}</span>
          <button type="button" className="ch-speaker" onClick={speakPrompt} aria-label="Read aloud">
            <SpeakerIcon size={18} />
          </button>
        </div>
      )}

      <StarPips total={payload.breaths} done={breathsDone} />

      <div
        className="ch-orb-zone"
        onPointerDown={(e) => setHold(true, e)}
        onPointerUp={() => setHold(false)}
        onPointerCancel={() => setHold(false)}
        onPointerLeave={() => setHold(false)}
      >
        <div className={`ch-orb-rays ${holding ? "is-active" : ""}`} aria-hidden="true" />
        <div className="ch-orb-halo" style={{ transform: `scale(${orbScale * 1.08})` }} />
        <svg className="ch-orb-ring" viewBox="0 0 300 300" aria-hidden="true">
          <circle cx="150" cy="150" r={R} fill="none" stroke="rgba(255,255,255,0.5)" strokeWidth="11" />
          <circle
            cx="150"
            cy="150"
            r={R}
            fill="none"
            stroke="url(#chOrbGrad)"
            strokeWidth="11"
            strokeLinecap="round"
            strokeDasharray={C}
            strokeDashoffset={C * (1 - progress)}
            transform="rotate(-90 150 150)"
          />
          <defs>
            <linearGradient id="chOrbGrad" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#ffd75d" />
              <stop offset="100%" stopColor="#f5aa21" />
            </linearGradient>
          </defs>
        </svg>
        <div className="ch-orb-sprite" style={{ transform: `scale(${orbScale})` }}>
          <Sprite name="ch_orb" />
        </div>
        <div className={`ch-motes ${holding ? "is-active" : ""}`} aria-hidden="true">
          {MOTES.map((i) => (
            <span key={i} className="ch-mote" style={{ "--ch-mote-x": `${(i * 149) % 200 - 100}px`, animationDelay: `${i * 0.55}s` }} />
          ))}
        </div>
      </div>

      <CaptionChip tone={holding ? "active" : ""}>
        {holding ? LABELS[phase] : "Rest your finger on the balloon"}
      </CaptionChip>
    </div>
  );
};

export default BreatheTrace;
